import { ApiError, NetworkError } from './api-error.js';

const BASE_URL = '/v1';

export type ListMeta = { page: number; limit: number; total: number; totalPages: number };
export type ListResult<T> = { data: T[]; meta: ListMeta };

type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** `FormData` yuborilganda `Content-Type` ni brauzer o'zi qo'yadi. */
  formData?: FormData;
  signal?: AbortSignal;
};

/**
 * API klienti.
 *
 * Access token XOTIRADA saqlanadi — `localStorage` da emas. Sabab: XSS
 * bo'lgan taqdirda `localStorage` dagi token o'g'irlanadi va hujumchi uni
 * boshqa qurilmada ishlatadi. Xotiradagi token esa sahifa yopilishi bilan
 * yo'qoladi; seans httpOnly refresh cookie orqali tiklanadi.
 */
class ApiClient {
  private accessToken: string | null = null;
  /** Bir vaqtda faqat BITTA yangilash so'rovi ketadi. */
  private refreshing: Promise<boolean> | null = null;
  private onUnauthorized: (() => void) | null = null;

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /** Seans butunlay tugaganda chaqiriladi — ilova login sahifasiga o'tadi. */
  setUnauthorizedHandler(handler: () => void): void {
    this.onUnauthorized = handler;
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.send(path, options);

    if (response.status === 401 && isRetryable(path)) {
      const refreshed = await this.refreshOnce();
      if (refreshed) return this.parse<T>(await this.send(path, options), path);

      this.accessToken = null;
      this.onUnauthorized?.();
    }

    return this.parse<T>(response, path);
  }

  get<T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'GET', query, signal });
  }

  post<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', body });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body });
  }

  delete<T>(path: string, query?: RequestOptions['query']): Promise<T> {
    return this.request<T>(path, { method: 'DELETE', query });
  }

  upload<T>(path: string, formData: FormData): Promise<T> {
    return this.request<T>(path, { method: 'POST', formData });
  }

  /** Fayl yuklab olish (XLSX eksport). */
  async download(path: string, query?: RequestOptions['query']): Promise<Blob> {
    const response = await this.send(path, { method: 'GET', query });
    if (response.status === 401) {
      if (await this.refreshOnce()) {
        return (await this.send(path, { method: 'GET', query })).blob();
      }
    }
    if (!response.ok) await this.parse(response, path);
    return response.blob();
  }

  private async send(path: string, options: RequestOptions): Promise<Response> {
    const url = BASE_URL + path + buildQuery(options.query);

    const headers: Record<string, string> = {};
    if (this.accessToken) headers.Authorization = `Bearer ${this.accessToken}`;
    if (options.body !== undefined) headers['Content-Type'] = 'application/json';

    try {
      return await fetch(url, {
        method: options.method ?? 'GET',
        headers,
        // Refresh cookie httpOnly — u faqat shu bayroq bilan yuboriladi
        credentials: 'include',
        body:
          options.formData ??
          (options.body === undefined ? undefined : JSON.stringify(options.body)),
        signal: options.signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new NetworkError(error);
    }
  }

  /**
   * Tokenni yangilaydi.
   *
   * Bir nechta so'rov birdan 401 olsa, ular BITTA yangilashni kutadi —
   * aks holda har biri o'z refresh so'rovini yuborardi va token rotatsiyasi
   * bir-birini bekor qilib, foydalanuvchi tizimdan chiqib ketardi.
   */
  private refreshOnce(): Promise<boolean> {
    this.refreshing ??= this.doRefresh().finally(() => {
      this.refreshing = null;
    });
    return this.refreshing;
  }

  private async doRefresh(): Promise<boolean> {
    try {
      const response = await this.send('/auth/refresh', { method: 'POST' });
      if (!response.ok) return false;

      const json = (await response.json()) as { data?: { accessToken?: string } };
      if (!json.data?.accessToken) return false;

      this.accessToken = json.data.accessToken;
      return true;
    } catch {
      return false;
    }
  }

  private async parse<T>(response: Response, path: string): Promise<T> {
    if (response.status === 204) return undefined as T;

    const text = await response.text();
    const json: unknown = text ? safeJson(text) : null;

    if (!response.ok) {
      const body = (json ?? {}) as { code?: string; message?: string; details?: unknown };
      throw new ApiError(
        response.status,
        body.code ?? `HTTP_${response.status}`,
        body.message ?? `So‘rov bajarilmadi: ${path}`,
        body.details,
      );
    }

    return json as T;
  }
}

/**
 * 401 dan keyin tokenni yangilab, so'rovni qaytarish MUMKINMI.
 *
 * Faqat ikkita yo'l chetlab o'tiladi:
 *   `/auth/refresh` — o'zini yangilashga urinish cheksiz aylanish bo'lardi;
 *   `/auth/login`   — noto'g'ri parol tokenni yangilash bilan tuzalmaydi.
 *
 * MUHIM: `/auth/me` bu ro'yxatda YO'Q. U ilova ochilganda chaqiriladi va
 * xotirada token bo'lmaydi — aynan shu so'rov cookie orqali seansni
 * tiklaydi. Agar u ham chetlab o'tilsa, foydalanuvchi sahifani har
 * yangilaganda qaytadan kirishga majbur bo'lardi.
 */
const NO_RETRY_PATHS = ['/auth/refresh', '/auth/login'];

export function isRetryable(path: string): boolean {
  return !NO_RETRY_PATHS.includes(path);
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

export function buildQuery(query: RequestOptions['query']): string {
  if (!query) return '';

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    // `undefined` va bo'sh satr — «filtr qo'yilmagan» degani, ular
    // manzilga tushmasligi kerak: aks holda `?status=` kabi bo'sh
    // parametrlar serverda validatsiyadan o'tmasdi.
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }

  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

export const api = new ApiClient();
